import React, { useMemo } from "react";
import ReactSelect, { FilterOptionOption, StylesConfig, ValueContainerProps } from "react-select";
import { FormProvider, useForm } from "react-hook-form";

import { Spinner } from "../../../Spinner";
import { SelectControl } from "./SelectControl";
import { SelectDropdownIndicator } from "./SelectDropdownIndicator";
import { SelectOption } from "./SelectOption";
import { SelectValueContainer } from "./SelectValueContainer";
import { Flex } from "~/components/Flex";
import { InputAdornment } from "../InputBase/InputAdornment";
import { Icon } from "~/components/Icon";
import { SvgClose } from "~/components/Icon/icons";
import { Tag } from "~/components/Tag";

export interface SelectBaseProps<TOption = unknown> {
  value?: any;
  defaultValue?: any;
  name?: string;
  placeholder?: string;
  disabled?: boolean;
  isFieldError?: boolean;
  size?: "xs" | "sm" | "md";
  variant?: "primary" | "success" | "danger" | "warning" | "neutral";
  options?: TOption[];
  style?: React.CSSProperties;
  isLoading?: boolean;
  isDirty?: boolean;
  isMulti?: boolean;
  components?: any;
  portalled?: boolean;
  isClearable?: boolean;
  closeMenuOnSelect?: boolean;
  menuPlacement?: "auto" | "top" | "bottom";
  hideSelectedOptions?: boolean;
  onChange?: (value: any) => void;
  onInputChange?: (value: string, actionMeta: import("react-select").InputActionMeta) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  loadingMessage?: (obj: { inputValue: string }) => React.ReactNode;
  getOptionLabel?: (option: TOption) => string;
  getOptionValue?: (option: TOption) => string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  dataCy?: string;
  placeholderFontSize?: string;
  noOptionsMessage?: string;
  filterOption?: ((option: FilterOptionOption<TOption>, inputValue: string) => boolean) | null;
  isOptionDisabled?: (option: TOption) => boolean;
  renderValuesOutside?: boolean;
  transparentBg?: boolean;
}

const SelectLoadingIndicator = () => <Spinner size="sm" />;

export const DEFAULT_COMPONENTS = {
  IndicatorSeparator: null,
  LoadingIndicator: SelectLoadingIndicator,
  DropdownIndicator: SelectDropdownIndicator,
  Control: SelectControl,
  Option: SelectOption,
  ValueContainer: SelectValueContainer,
};

export const SelectBase = <TOption,>({
  disabled,
  isDirty,
  portalled = false,
  components = DEFAULT_COMPONENTS,
  style,
  formatOptionLabel,
  leftIcon,
  rightIcon,
  dataCy,
  placeholderFontSize,
  noOptionsMessage,
  isMulti,
  value,
  onChange,
  getOptionLabel = (option: any) => option.label,
  getOptionValue = (option: any) => option.value,
  renderValuesOutside = false,
  hideSelectedOptions,
  ...props
}: SelectBaseProps<TOption> & { formatOptionLabel?: (option: TOption) => React.ReactNode }) => {
  const formData = useForm({ defaultValues: { dataCy } });

  const color = {
    primary: "var(--colors-primary-600)",
    success: "var(--colors-success-600)",
    danger: "var(--colors-danger-600)",
    warning: "var(--colors-warning-600)",
    neutral: "var(--colors-neutral-600)",
  };

  const disabledColor = {
    primary: "var(--colors-primary-300)",
    success: "var(--colors-success-300)",
    danger: "var(--colors-danger-300)",
    warning: "var(--colors-warning-300)",
    neutral: "var(--colors-neutral-300)",
  };

  const stylesConfig: StylesConfig<any> = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 99999,
      pointerEvents: "auto",
    }),
    container: (base) => ({
      ...base,
      ...style,
    }),
    menuList: () => ({
      maxHeight: "220px",
      overflow: "auto",
    }),
    dropdownIndicator: () => ({}),
    control: () => ({}),
    valueContainer: () => ({}),
    option: () => ({
      whiteSpace: "pre-line",
    }),
    multiValue: () => ({}),
    multiValueRemove: () => ({}),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled
        ? props.variant
          ? disabledColor[props.variant]
          : "var(--colors-neutral-300)"
        : props.variant
          ? color[props.variant]
          : "var(--colors-neutral-900)",
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--colors-neutral-500)",
      ...(placeholderFontSize && { fontSize: placeholderFontSize }),
    }),
  };

  const CustomValueContainer = useMemo(() => {
    return (props: ValueContainerProps) => (
      <Flex
        align="center"
        justify="between"
        css={{
          flex: 1,
          boxSizing: "border-box",
          display: "flex",
          flexWrap: "nowrap",
          position: "relative",
          overflow: "hidden",
          "@mxmd": {
            padding: "0 $2",
          },
        }}
      >
        {leftIcon && (
          <InputAdornment position="start" disablePointerEvents>
            {leftIcon}
          </InputAdornment>
        )}
        <SelectValueContainer {...props} />
      </Flex>
    );
  }, []);

  const CustomDropdownIndicator = (props: any) => (
    <Flex align="center" justify="center">
      <SelectDropdownIndicator {...props} />
      {rightIcon && <span style={{ marginLeft: 4 }}>{rightIcon}</span>}
    </Flex>
  );

  const mergedComponents = {
    ...components,
    ValueContainer: leftIcon
      ? CustomValueContainer
      : components?.ValueContainer || SelectValueContainer,
    DropdownIndicator: rightIcon
      ? CustomDropdownIndicator
      : components?.DropdownIndicator || SelectDropdownIndicator,
  };

  const handleRemoveExternalItem = (itemToRemove: TOption) => {
    if (!onChange || !Array.isArray(value)) return;

    const newValue = value.filter((item) => getOptionValue(item) !== getOptionValue(itemToRemove));
    onChange(newValue);
  };

  return (
    <FormProvider {...formData}>
      <ReactSelect
        {...props}
        value={value}
        onChange={onChange}
        isMulti={isMulti}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        hideSelectedOptions={renderValuesOutside ? true : hideSelectedOptions}
        controlShouldRenderValue={renderValuesOutside && isMulti ? false : true}
        data-cy={dataCy}
        isDisabled={disabled}
        data-dirty={isDirty}
        styles={stylesConfig}
        components={{ ...mergedComponents }}
        menuPosition="absolute"
        menuPortalTarget={portalled ? document.body : undefined}
        formatOptionLabel={formatOptionLabel}
        noOptionsMessage={() => noOptionsMessage || "Nenhuma opção encontrada"}
        loadingMessage={() => "Carregando..."}
      />

      {renderValuesOutside && isMulti && Array.isArray(value) && value.length > 0 && (
        <Flex wrap="wrap" gap="2" css={{ marginTop: "$2" }}>
          {value.map((item) => (
            <Tag
              key={getOptionValue(item)}
              variant="info-light"
              onClick={() => handleRemoveExternalItem(item)}
              css={{
                height: "$6",
                py: 0,
                backgroundColor: "transparent",
              }}
            >
              {getOptionLabel(item)}
              <Icon as={SvgClose} size="sm" css={{ cursor: "pointer" }} />
            </Tag>
          ))}
        </Flex>
      )}
    </FormProvider>
  );
};

SelectBase.displayName = "SelectBase";
